import React, { useState, useEffect, useRef } from "react";
import { Zap, Lock, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../utils/api";
import { usePublicAuth } from "../context/PublicAuthContext";

/**
 * HypeButton – Supporter können 3 Releases/Monat hypen.
 *
 * Für Nicht-Supporter / nicht-eingeloggte User:
 *   → Hover-Tooltip mit Info + Link zur Paywall. Kein Klick-Redirect.
 *
 * Für Supporter:
 *   → Prominenter Amber-CTA wenn noch Hypes übrig.
 */
export default function HypeButton({
  releaseId,
  size = 16,
  showCount = true,
  showLabel = false,
  compact = false,
  className = "",
  onStatusLoad,
}) {
  const { publicUser, isPublicAuth, publicApi, login, interactionCache, updateInteractionCache } = usePublicAuth();
  const { t } = useTranslation();
  const isSupporter = publicUser?.is_supporter;

  const cached = interactionCache[releaseId] || {};
  const [hyped, setHyped] = useState(cached.hyped || false);
  const [count, setCount] = useState(cached.hypeCount || 0);
  const [remaining, setRemaining] = useState(cached.remaining ?? null);
  const [loading, setLoading] = useState(false);
  const [animating, setAnimating] = useState(false);
  const [error, setError] = useState(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipTimerRef = useRef(null);

  const handleTooltipEnter = () => {
    if (tooltipTimerRef.current) clearTimeout(tooltipTimerRef.current);
    setShowTooltip(true);
  };
  const handleTooltipLeave = () => {
    tooltipTimerRef.current = setTimeout(() => setShowTooltip(false), 180);
  };

  // Sync state when global cache changes
  useEffect(() => {
    const c = interactionCache[releaseId];
    if (!c) return;
    if (c.hyped !== undefined) setHyped(c.hyped);
    if (c.hypeCount !== undefined) setCount(c.hypeCount);
    if (c.remaining !== undefined) setRemaining(c.remaining);
  }, [interactionCache[releaseId]?.hyped, interactionCache[releaseId]?.hypeCount, interactionCache[releaseId]?.remaining]); // eslint-disable-line

  // Load status on mount
  useEffect(() => {
    if (!releaseId) return;
    let cancelled = false;
    const load = async () => {
      try {
        const res = isPublicAuth
          ? await publicApi("get", `/hype/status/${releaseId}`)
          : await api.get(`/hype/status/${releaseId}`);
        if (!cancelled) {
          const newHyped = res.data.user_hyped || false;
          const newCount = res.data.hype_count || 0;
          const newRemaining = typeof res.data.remaining === "number" ? res.data.remaining : null;
          setCount(newCount); setHyped(newHyped);
          if (newRemaining !== null) setRemaining(newRemaining);
          updateInteractionCache(releaseId, { hyped: newHyped, hypeCount: newCount, remaining: newRemaining });
          if (onStatusLoad) onStatusLoad(res.data);
        }
      } catch { /* silent */ }
    };
    load();
    return () => { cancelled = true; };
  }, [releaseId, isPublicAuth]); // eslint-disable-line

  const handleHype = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (!isPublicAuth) { login({ appState: { returnTo: window.location.pathname } }); return; }
    if (!isSupporter || loading) return;
    setLoading(true); setAnimating(true); setError(null);
    try {
      const res = await publicApi("post", `/hype/${releaseId}`);
      const newHyped = res.data.hyped;
      const newCount = res.data.hype_count;
      const newRemaining = res.data.remaining;
      setHyped(newHyped); setCount(newCount); setRemaining(newRemaining);
      updateInteractionCache(releaseId, { hyped: newHyped, hypeCount: newCount, remaining: newRemaining });
    } catch (err) {
      const msg = err.response?.data?.detail;
      if (msg) setError(msg);
    } finally {
      setLoading(false);
      setTimeout(() => setAnimating(false), 500);
    }
  };

  // ── Tooltip für Nicht-Supporter ──────────────────────────────────────────────
  const UpsellTooltip = () => (
    <div
      className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2.5 w-56 bg-zinc-900 border border-white/10 rounded-2xl px-4 py-3 shadow-2xl z-[100] pointer-events-auto"
      style={{ boxShadow: "0 16px 48px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.06)" }}
      onMouseEnter={handleTooltipEnter}
      onMouseLeave={handleTooltipLeave}
    >
      <div className="flex items-center gap-2 mb-1.5">
        <Lock size={11} className="text-zinc-500 flex-shrink-0" />
        <p className="text-xs font-bold text-white">{t("hype.supporter_only")}</p>
      </div>
      <p className="text-zinc-500 text-xs leading-relaxed mb-2.5">{t("hype.supporter_desc")}</p>
      <Link
        to="/supporter"
        onClick={() => setShowTooltip(false)}
        className="text-xs font-semibold text-rose-400 hover:text-rose-300 transition-colors"
      >
        {t("hype.become_supporter")}
      </Link>
      {/* Arrow */}
      <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0"
        style={{ borderLeft: "5px solid transparent", borderRight: "5px solid transparent", borderTop: "5px solid rgba(255,255,255,0.10)" }} />
    </div>
  );

  // ── Nicht eingeloggt oder kein Supporter ────────────────────────────────────
  if (!isPublicAuth || !isSupporter) {
    return (
      <div
        className={`relative ${className}`}
        onMouseEnter={handleTooltipEnter}
        onMouseLeave={handleTooltipLeave}
      >
        <div
          className={`flex items-center gap-1 cursor-default ${compact ? "text-zinc-700" : "px-3 py-2 rounded-xl bg-zinc-900/40 border border-white/[0.04]"}`}
          data-testid={`hype-locked-${releaseId}`}
        >
          <Lock size={size - 3} className="text-zinc-800" />
          <Zap size={size} className="text-zinc-700" />
          {showCount && count > 0 && <span className="text-xs tabular-nums text-zinc-700">{count}</span>}
          {showLabel && !compact && <span className="text-xs text-zinc-700">Hype</span>}
        </div>
        {showTooltip && <UpsellTooltip />}
      </div>
    );
  }

  // ── Supporter – Status ermitteln ─────────────────────────────────────────────
  const noHypesLeft = !hyped && remaining === 0;

  // Bereits gehyped
  if (hyped) {
    return (
      <div className={`flex flex-col gap-1 ${className}`}>
        <button onClick={handleHype} disabled={loading}
          data-testid={`hype-btn-${releaseId}`}
          title="Hype entfernen"
          style={{ transform: animating ? "scale(1.3)" : "scale(1)", transition: "transform 0.3s cubic-bezier(.36,.07,.19,.97)" }}
          className={`flex items-center gap-1.5 transition-all text-amber-400 hover:text-amber-500 ${compact ? "" : "px-3 py-2 rounded-xl"}`}>
          {loading ? <Loader2 size={size} className="animate-spin" /> : <Zap size={size} fill="currentColor" />}
          {showCount && count > 0 && <span className="text-xs font-bold tabular-nums">{count}</span>}
          {showLabel && <span className="text-xs font-semibold">{t("hype.hyped")}</span>}
          {!compact && remaining !== null && <span className="text-zinc-700 text-xs ml-0.5">({remaining}/3)</span>}
        </button>
      </div>
    );
  }

  // Keine Hypes mehr übrig
  if (noHypesLeft) {
    return (
      <div data-testid={`hype-btn-${releaseId}`}
        title="Keine Hypes diesen Monat mehr übrig"
        className={`flex items-center gap-1.5 text-zinc-600 cursor-not-allowed opacity-50 ${className}`}>
        <Zap size={size} />
        {showCount && count > 0 && <span className="text-xs tabular-nums">{count}</span>}
        {showLabel && <span className="text-xs">0/3</span>}
      </div>
    );
  }

  // Kann hypen – PROMINENTER CTA ───────────────────────────────────────────────
  if (compact) {
    return (
      <button onClick={handleHype} disabled={loading}
        data-testid={`hype-btn-${releaseId}`}
        title={`Jetzt hypen (${remaining ?? "?"}/3 übrig)`}
        style={{ transform: animating ? "scale(1.35)" : "scale(1)", transition: "transform 0.3s cubic-bezier(.36,.07,.19,.97)" }}
        className={`flex items-center gap-1 text-amber-400 hover:text-amber-300 transition-colors ${className}`}>
        {loading ? <Loader2 size={size} className="animate-spin" /> : <Zap size={size} />}
        {showCount && count > 0 && <span className="text-xs font-bold tabular-nums">{count}</span>}
      </button>
    );
  }

  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      <button onClick={handleHype} disabled={loading}
        data-testid={`hype-btn-${releaseId}`}
        title={`Jetzt hypen (${remaining ?? "?"}/3 übrig diesen Monat)`}
        style={{
          transform: animating ? "scale(1.05)" : "scale(1)",
          transition: "transform 0.3s cubic-bezier(.36,.07,.19,.97)",
          boxShadow: "0 0 18px rgba(245,158,11,0.18), 0 2px 8px rgba(0,0,0,0.4)",
        }}
        className="flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-sm
          bg-amber-500/15 border border-amber-500/35 text-amber-300
          hover:bg-amber-500/25 hover:border-amber-400/55 hover:text-amber-200
          active:scale-95 transition-all group">
        {loading
          ? <Loader2 size={14} className="animate-spin" />
          : <Zap size={14} className="group-hover:fill-amber-300 transition-all" />
        }
        <span>{t("hype.hype_now")}</span>
        {remaining !== null && <span className="text-amber-600 text-xs font-normal ml-0.5">{remaining}/3</span>}
        {showCount && count > 0 && <span className="text-amber-600 text-xs font-normal ml-auto">{count} ⚡</span>}
      </button>
      {error && <p className="text-xs text-rose-400 max-w-[220px] leading-tight">{error}</p>}
    </div>
  );
}
